
import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { AudioTrack } from '@/data/types';

interface AudioPlayerProps {
  language: string;
  t: (key: string) => string;
  audioUrl: string;
  track?: AudioTrack;
}

const AudioPlayer = ({ language, t, audioUrl, track }: AudioPlayerProps) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  
  console.log('AudioPlayer rendering - NO LABELS SHOULD BE VISIBLE');
  
  // Create audio element
  useEffect(() => {
    const audio = new Audio(audioUrl);
    audioRef.current = audio;
    
    // Set up audio event listeners
    audio.addEventListener('loadedmetadata', () => {
      setDuration(audio.duration);
    });
    
    audio.addEventListener('timeupdate', () => {
      setCurrentTime(audio.currentTime);
      setProgress((audio.currentTime / audio.duration) * 100);
    });
    
    audio.addEventListener('ended', () => {
      setIsPlaying(false);
      setProgress(0);
      setCurrentTime(0);
    });
    
    return () => {
      audio.pause();
      audio.removeEventListener('loadedmetadata', () => {});
      audio.removeEventListener('timeupdate', () => {});
      audio.removeEventListener('ended', () => {});
    };
  }, [audioUrl]);

  
  // Handle play/pause toggle
  const handleAudioToggle = () => {
    if (!audioRef.current) return;
    
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    
    setIsPlaying(!isPlaying);
  };

  // Handle seeking when user drags or clicks the slider
  const handleSeek = (value: number[]) => {
    if (!audioRef.current) return;
    
    const newProgress = value[0];
    const seekTime = (newProgress / 100) * duration;
    
    audioRef.current.currentTime = seekTime;
    setProgress(newProgress);
    setCurrentTime(seekTime);
  };
  
  // Format time to display as MM:SS
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs < 10 ? '0' + secs : secs}`;
  };

  const currentTimeFormatted = formatTime(currentTime);
  const totalTimeFormatted = formatTime(duration);

  return (
    <div className="my-6">
      {/* Force LTR direction for the audio player regardless of page language */}
      <div className="flex items-center gap-3 md:gap-4" style={{ direction: 'ltr' }}>
        {/* Play/Pause button (always on the left side) */}
        <Button 
          variant="outline"
          size="icon"
          onClick={handleAudioToggle}
          className="flex-shrink-0 bg-blue-600 hover:bg-blue-700 text-white border-0 h-12 w-12 rounded-full shadow-md transition-transform hover:scale-105"
        >
          {isPlaying ? 
            <Pause className="h-6 w-6" /> : 
            <Play className="h-6 w-6 ml-0.5" />
          }
        </Button>
        
        {/* Time elapsed (always left of slider) */}
        <span className="text-sm font-medium text-gray-600">
          {currentTimeFormatted}
        </span>
        
        {/* Progress slider (takes available space) */}
        <div className="flex-grow">
          <Slider 
            value={[progress]} 
            min={0} 
            max={100} 
            step={0.1}
            onValueChange={handleSeek}
          />
        </div>
        
        {/* Total duration (always right of slider) */}
        <span className="text-sm font-medium text-gray-600">
          {totalTimeFormatted}
        </span>

      </div>
    </div>
  );
};

export default AudioPlayer;
